import type React from 'react';
import { useCallback, useState } from 'react';

import {
  FileTextIcon,
  FileCodeIcon,
  FileIcon,
  FileTypeIcon,
  ImageIcon,
  Trash2Icon,
} from 'lucide-react';
import { useDropzone } from 'react-dropzone';
import { useTranslation } from 'react-i18next';

import { cn } from '@/lib/utils';

import EmptyState from './EmptyState';
import { acceptedExtensionsOf } from './useFileUpload';
import { Button } from '../../button';

// Helper function to format file size
const formatFileSize = (sizeInBytes: number): string => {
  const sizeInMB = sizeInBytes / 1024 / 1024;

  if (sizeInMB < 1) {
    // Show in KB for files smaller than 1 MB
    const sizeInKB = sizeInBytes / 1024;
    return `${sizeInKB.toFixed(2)} KB`;
  }

  // Show in MB for files 1 MB or larger
  return `${sizeInMB.toFixed(2)} MB`;
};

// Define the type for the format mapping (MIME type to file extensions)
export type AcceptedFormats = Record<string, string[]>;

type SingleFileUploadProps = {
  /**
   * Callback function called when a file is selected
   */
  onFileSelect: (file: File | null) => void;

  /**
   * Maximum file size in MB
   */
  maxSizeMB?: number;

  /**
   * Map of MIME types to their extensions, e.g. { 'text/csv': ['.csv'] }
   */
  acceptedFormats?: AcceptedFormats;

  /**
   * Current selected file (for controlled mode)
   */
  file?: File | null;

  /**
   * Error message to display
   */
  error?: string;

  /**
   * Whether the component is disabled
   */
  disabled?: boolean;

  /**
   * Placeholder text for when no file is selected
   */
  placeholder?: string;

  /**
   * Success title key to display
   */
  successTitleKey?: string;

  /**
   * Whether the dropzone lists the accepted file extensions
   */
  showAcceptedFormats?: boolean;
};

const FILE_ICON_CLASSES = 'h-4 w-4 text-muted-foreground' as const;

const getFileIcon = (fileName: string) => {
  const extension = fileName.split('.').pop()?.toLowerCase() || '';

  switch (extension) {
    case 'pdf':
    case 'doc':
    case 'docx':
      return <FileIcon className={FILE_ICON_CLASSES} />;
    case 'html':
    case 'md':
      return <FileCodeIcon className={FILE_ICON_CLASSES} />;
    case 'txt':
    case 'csv':
      return <FileTextIcon className={FILE_ICON_CLASSES} />;
    case 'jpg':
    case 'jpeg':
    case 'png':
    case 'gif':
    case 'webp':
    case 'svg':
      return <ImageIcon className={FILE_ICON_CLASSES} />;
    default:
      return <FileTypeIcon className={FILE_ICON_CLASSES} />;
  }
};

/**
 * SingleFileUpload
 *
 * Controlled single-file upload with drag-and-drop and file preview. Can optionally hide the
 * size line (useful for virtual files synthesized from other inputs).
 *
 * @param onFileSelect Callback invoked when a file is selected or removed
 * @param maxSizeMB Maximum allowed file size in MB
 * @param acceptedFormats Mapping of MIME types to extensions
 * @param file Current selected file (controlled)
 * @param error Error message to display
 * @param disabled Disable interactions
 * @param placeholder Placeholder text when idle
 * @param successTitleKey Title displayed when file is present
 * @returns JSX.Element
 */
const SingleFileUpload = ({
  onFileSelect,
  maxSizeMB = 10,
  acceptedFormats = { 'text/csv': ['.csv'] },
  file,
  error,
  disabled = false,
  placeholder,
  successTitleKey,
  showAcceptedFormats,
}: SingleFileUploadProps) => {
  const { t } = useTranslation();
  const [isDragActive, setIsDragActive] = useState(false);

  // Get accepted extensions for display
  const acceptedExtensions = acceptedExtensionsOf(acceptedFormats);

  const handleFiles = useCallback(
    (acceptedFiles: File[]) => {
      setIsDragActive(false); // Reset drag state after drop
      if (acceptedFiles.length > 0) {
        const selectedFile = acceptedFiles[0];

        // Check file size
        if (selectedFile.size > maxSizeMB * 1024 * 1024) {
          onFileSelect(null);
          return;
        }

        onFileSelect(selectedFile);
      }
    },
    [onFileSelect, maxSizeMB]
  );

  const { getRootProps, getInputProps } = useDropzone({
    onDrop: handleFiles,
    accept: Object.fromEntries(Object.keys(acceptedFormats).map((type) => [type, []])),
    maxSize: maxSizeMB * 1024 * 1024,
    multiple: false,
    disabled,
    onDragEnter: () => setIsDragActive(true),
    onDragLeave: () => setIsDragActive(false),
  });

  const handleRemoveFile = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      setIsDragActive(false); // Reset drag state when file is removed
      onFileSelect(null);
    },
    [onFileSelect]
  );

  const hasFile = !!file;

  return (
    <>
      {!hasFile && (
        <EmptyState
          {...getRootProps()}
          status="idle"
          isDragActive={isDragActive}
          acceptedExtensions={acceptedExtensions}
          showAcceptedFormats={showAcceptedFormats}
          maxSizeMB={maxSizeMB}
          placeholder={placeholder}
          disabled={disabled}
        >
          <input {...getInputProps()} />
        </EmptyState>
      )}

      {/* File display section */}
      {hasFile && (
        <div className="group bg-muted/40">
          <div className="flex items-center justify-between rounded-lg border p-3">
            <div className="flex min-w-0 flex-1 items-center gap-3">
              <span className="shrink-0">{getFileIcon(file.name)}</span>
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-medium">{successTitleKey}</p>
                {file.size > 0 && (
                  <p className="text-xs text-muted-foreground">{formatFileSize(file.size)}</p>
                )}
              </div>
            </div>

            {/* Remove button - only visible on hover */}
            <Button
              className={cn(
                'ml-3 shrink-0 bg-background opacity-0 transition-opacity duration-200 group-hover:opacity-100 hover:text-destructive hover:opacity-100 focus:opacity-100 focus-visible:opacity-100 hover:[&_svg]:text-destructive',
                { '-translate-y-8': file.size }
              )}
              onClick={handleRemoveFile}
              aria-label={`${t('common.remove')} ${file.name}`}
              variant="outline-neutral"
              size="icon-xss"
            >
              <Trash2Icon size={16} />
            </Button>
          </div>
        </div>
      )}

      {/* Warning help text only — no error styling on the dropzone. */}
      {error && <div className="mt-2 text-sm text-warning">{error}</div>}
    </>
  );
};

export default SingleFileUpload;
